/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import {
  gameTimeZone,
  isGameTimeTbd,
} from '@sportninja/common/features/game/gameListView/gameDayBucket'
import type { GameLike } from '@sportninja/common/features/game/gameListView/types'
import { deriveGameStatus } from '@sportninja/common/features/game/gameListView/gameStatus'
import { formatVenueLocal } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'

import {
  font,
  weightMedium,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'
// Deep import on purpose. The feature barrel re-exports `CalendarPanel`, which
// imports this folder's barrel, which reaches this file — so importing
// `GameStatusTag` from the barrel would close that cycle.
import GameStatusTag from 'src/features/game/gameListView/components/GameStatusTag'
import GameListTeamLogo from '../GameListTeamLogo'
import { useCalendarDisplay } from './CalendarDisplayContext'
import type { CalendarGameCardProps } from './types'
import { calendarLabel } from './calendarI18n'
import { resolveVisitingTeamDisplay } from '@sportninja/common/features/game/placeholderVisitor/resolveVisitingTeamDisplay'

/**
 * Sized to sit INSIDE the matchup line's 12px/1.3 line box (15.6px), so adding
 * logos leaves the card's height — and therefore each cell's "+N more" cut-off
 * — exactly where it was.
 */
const LOGO_STYLE = {
  width: 14,
  height: 14,
  borderRadius: 3,
  flex: 'none',
} as const

const CalendarGameCard = ({ game }: CalendarGameCardProps) => {
  'use memo'

  const { showSeasonName } = useCalendarDisplay()
  const asGame = game as unknown as GameLike
  const isTbd = isGameTimeTbd(asGame)

  /*
   * Only when there is something to say.
   *
   * A "Scheduled" tag on every card would cost a line in a 110px cell to
   * restate what a future game on a calendar already means, and it would make
   * the cards that DO carry news — live, final, postponed, cancelled — no
   * easier to pick out. Absence is the scheduled state.
   */
  const status = deriveGameStatus(asGame)
  const showStatus = status.key !== 'scheduled' || status.inReview

  //  Venue-local, not browser-local: the card sits in a cell keyed by the
  //  venue's day, so showing the viewer's clock would put a 7pm Vancouver game
  //  under "10pm" in a cell labelled the previous date.
  const time = isTbd
    ? calendarLabel('calendarTbd')
    : formatVenueLocal(asGame.starts_at, gameTimeZone(asGame), 'h:mm a')

  const home =
    game.homeTeam?.name_full ||
    game.homeTeamSlot?.name_full ||
    calendarLabel('calendarTbd')
  //  Placeholder name sits between the slot and the TBD label: a real team
  //  wins, then a bracket slot, then the admin-typed name (SN-8410).
  const away =
    game.visitingTeam?.name_full ||
    game.visitingTeamSlot?.name_full ||
    resolveVisitingTeamDisplay(game).name ||
    calendarLabel('calendarTbd')
  const venue = game.venue?.name || ''
  //  The surface is the half of "where" that decides where you walk once you
  //  are in the building, and List and Details have always shown it.
  const surface = game.facility?.name || ''

  /*
   * The COMPETITION, which is `rootSchedule` — `schedule` is the division
   * ("Division 7 19+"), and a division name answers the wrong question when
   * the calendar is showing several seasons at once. The fallback is for the
   * endpoints that do not send a root schedule; a division still tells the two
   * cards apart more than nothing does.
   */
  const seasonName = showSeasonName
    ? game.rootSchedule?.name || game.schedule?.name || ''
    : ''

  return (
    <Link to={`${ROUTES.GAME_ROOT}/${game.id}`} css={cardStyles(isTbd)}>
      {seasonName && (
        <div
          css={seasonStyles}
          data-testid='calendar-card-season'
          //  A month cell is ~110px wide, so this line truncates there — and
          //  "PAHL 2025-26 re…" against "PAHL 2025-26 PL…" is exactly the pair
          //  a reader needs to be sure about.
          title={seasonName}
        >
          {seasonName}
        </div>
      )}
      <div css={timeRowStyles}>
        <span css={timeStyles}>{time}</span>
        {showStatus && <GameStatusTag status={status} size='card' />}
      </div>
      <div css={matchupStyles}>
        <GameListTeamLogo
          team={game.homeTeam}
          style={LOGO_STYLE}
          hideWhenMissing
        />
        <span css={teamNameStyles}>{home}</span>
        <span css={versusStyles}>{calendarLabel('calendarVersus')}</span>
        <GameListTeamLogo
          team={game.visitingTeam}
          style={LOGO_STYLE}
          hideWhenMissing
        />
        <span css={teamNameStyles}>{away}</span>
      </div>
      {(venue || surface) && (
        <div css={locationStyles} data-testid='calendar-card-location'>
          {venue && (
            <span css={venuePartStyles} title={venue}>
              {venue}
            </span>
          )}
          {venue && surface && <span css={separatorStyles}>·</span>}
          {surface && (
            <span css={surfacePartStyles} data-testid='calendar-card-surface'>
              {surface}
            </span>
          )}
        </div>
      )}
    </Link>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const cardStyles = (isTbd: boolean) => css`
  display: block;
  text-decoration: none;
  background: ${colors.SECONDARY_400};
  border-left: 3px solid ${isTbd ? colors.NEUTRAL_100 : colors.PRIMARY};
  border-radius: 4px;
  padding: 5px 8px;
  cursor: pointer;
  transition: background 0.15s ease;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;

  &:hover {
    background: ${colors.SECONDARY_300};
  }
`

/*
 * Above the time, muted and in small caps — the same shape the List row gives
 * this label, so the two views read as one product. Small enough that a card
 * gaining a line does not change how many games a month cell can show before
 * it starts counting "+N more".
 */
const seasonStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 10px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`

/*
 * The time and the status share a line rather than stacking: a month cell shows
 * ~3 cards before it starts hiding them behind "+N more", and a card that grew
 * a row would cut that to 2.
 */
const timeRowStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  /*
   * The badge follows the TIME rather than being pushed to the far edge. In the
   * month grid the two are inches apart either way, but the agenda row runs the
   * full width of the page, and a status marooned at the right margin reads as
   * belonging to the row rather than to the game.
   *
   * It also gives up width first: a truncated "Postpone…" beside a whole time
   * beats a whole status over a cut-off "3:1…".
   */
  > *:last-child {
    min-width: 0;
    flex: 0 1 auto;
  }
`

const timeStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 13px;
  color: ${colors.PRIMARY};
  line-height: 1.2;
  flex: none;
`

const matchupStyles = css`
  ${font.body}
  ${weightMedium}
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 13px;
  color: ${colors.NEUTRAL_0};
  line-height: 1.3;
`

/*
 * Both names shrink rather than the line truncating as one run. Flex shrinks in
 * proportion to content width, so the longer name gives up characters first and
 * a long-vs-short matchup keeps the short name whole — where a single truncated
 * run would have hidden the second team entirely.
 */
const teamNameStyles = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const versusStyles = css`
  flex: none;
  color: ${colors.NEUTRAL_70};
`

const locationStyles = css`
  ${font.body}
  ${weightRegular}
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
  line-height: 1.2;
`

/*
 * The VENUE gives up characters first, so the surface survives a 110px month
 * cell — "Planet Ic… · Rink #2" rather than "Planet Ice - Coqu…" with the rink
 * cut off entirely. Appending the surface to one truncating run made the half
 * the reader came for the first thing to disappear.
 */
const venuePartStyles = css`
  flex: 0 999 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const surfacePartStyles = css`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/*
 * A middot, where the List row uses " - ". Venue names carry hyphens of their
 * own ("Planet Ice - Coquitlam"), and on a card narrow enough to truncate both
 * halves a third hyphen reads as part of the name.
 */
const separatorStyles = css`
  flex: none;
  color: ${colors.NEUTRAL_100};
`

export default CalendarGameCard
